import { useGetList, type Identifier } from "ra-core";

import { cn } from "@/lib/utils";
import type { Sale, TimeEntry } from "../../types";
import { entrySeconds, formatDuration } from "../../workItems/timeFormat";
import { Initials } from "./FocusRow";
import type { FocusRow } from "./ranking";

// «Teamet» (fiks 9): én linje per aktiv bruker med forfalte gjøremål, en tynn
// strek for timer denne uken mot planlagt uke, og timene som tekst. Et klikk
// på en person filtrerer «Gjør nå» til den personen. Bare på stor skjerm.

/** Standard arbeidsuke når personen ikke har satt sin egen. */
export const DEFAULT_WEEK_HOURS = 37.5;

const isOverdue = (row: FocusRow) =>
  row.signals.some(
    (s) => s.kind === "task_overdue" || s.kind === "next_step_overdue",
  );

/** Antall forfalte gjøremål per person. */
export const overdueByOwner = (rows: FocusRow[]) => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (row.ownerId == null || !isOverdue(row)) continue;
    const key = String(row.ownerId);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
};

export const TeamWorkload = ({
  sales,
  rows,
  monday,
  person,
  onPerson,
}: {
  sales: Sale[];
  /** Alle rader i Team, også Rydd opp. */
  rows: FocusRow[];
  /** Mandag denne uken (Oslo-dato). */
  monday: string;
  person: Identifier | null;
  onPerson: (id: Identifier | null) => void;
}) => {
  const { data: entries = [] } = useGetList<TimeEntry>("time_entries", {
    filter: { "started_at@gte": `${monday}T00:00:00` },
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "started_at", order: "ASC" },
  });
  const overdue = overdueByOwner(rows);
  const seconds = new Map<string, number>();
  for (const e of entries) {
    const key = String(e.sales_id);
    seconds.set(
      key,
      (seconds.get(key) ?? 0) + entrySeconds(e.started_at, e.ended_at),
    );
  }
  const people = sales.filter((s) => !s.disabled);
  if (people.length < 2) return null;

  return (
    <section
      aria-labelledby="teamet-title"
      className="hidden flex-col gap-3 lg:flex"
    >
      <h2 id="teamet-title" className="text-[13px] font-semibold">
        Teamet
        <span className="ml-2 font-normal text-muted-foreground">
          Forfalte gjøremål og timer ført denne uken
        </span>
      </h2>
      <ul className="overflow-hidden rounded-[10px] border bg-card">
        {people.map((sale) => {
          const name = `${sale.first_name} ${sale.last_name}`.trim();
          const count = overdue.get(String(sale.id)) ?? 0;
          const done = seconds.get(String(sale.id)) ?? 0;
          const planned =
            (sale.planned_week_hours ?? DEFAULT_WEEK_HOURS) * 3600;
          const share = planned > 0 ? Math.min(1, done / planned) : 0;
          const active = person != null && String(person) === String(sale.id);
          return (
            <li key={sale.id} className="border-b last:border-b-0">
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onPerson(active ? null : sale.id)}
                title={
                  active ? "Vis alle igjen" : `Vis bare ${name} i «Gjør nå»`
                }
                className={cn(
                  "grid w-full grid-cols-[24px_minmax(0,1fr)_96px_150px_150px] items-center gap-4 px-4 py-2.5 text-left text-[13px] hover:bg-muted/40",
                  active && "bg-muted/50",
                )}
              >
                <Initials name={name} />
                <span className="truncate font-medium">{name}</span>
                <span
                  className={cn(
                    "tabular-nums",
                    count >= 5 ? "text-red-500" : "text-muted-foreground",
                  )}
                >
                  {count} forfalt
                </span>
                <span
                  className="h-1 w-full overflow-hidden rounded-full bg-muted"
                  role="img"
                  aria-label={`${Math.round(share * 100)} % av planlagt uke`}
                >
                  <span
                    className="block h-full rounded-full bg-foreground/70"
                    style={{ width: `${share * 100}%` }}
                  />
                </span>
                <span className="text-right text-muted-foreground tabular-nums">
                  {formatDuration(done)} denne uken
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
